import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";
import { apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import BrandMark from "@/components/common/BrandMark";

export default function Login() {
  const { login, verifyMfa, user } = useAuth();
  const { t, locale, setLocale } = useI18n();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [mfaChallenge, setMfaChallenge] = useState("");
  const [mfaCode, setMfaCode] = useState("");

  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result?.mfa_required) {
        setMfaChallenge(result.challenge_token);
        return;
      }
      navigate("/", { replace: true });
    } catch (err) {
      setError(apiErrorMessage(err, t));
    } finally {
      setLoading(false);
    }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await verifyMfa(mfaChallenge, mfaCode);
      navigate("/", { replace: true });
    } catch (err) {
      setError(apiErrorMessage(err, t));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-950">
      <div className="hidden w-1/2 flex-col justify-between bg-slate-900 p-12 text-white lg:flex">
        <div className="flex items-center gap-4">
          <BrandMark className="h-28 w-28" />
          <span className="font-heading text-xl font-bold">Mayr-Technologies</span>
        </div>
        <div>
          <h2 className="font-heading text-3xl font-bold leading-tight">{t("app.tagline")}</h2>
          <ul className="mt-6 max-w-lg space-y-3 text-sm text-slate-300">
            {[1, 2, 3, 4, 5].map((number) => (
              <li key={number} className="flex items-start gap-2.5">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
                <span>{t(`app.benefit_${number}`)}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="text-xs text-slate-400">© {new Date().getFullYear()} Mayr-Technologies</div>
      </div>

      <div className="flex w-full items-center justify-center p-6 lg:w-1/2">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex items-center justify-between">
            <div className="flex items-center gap-3 lg:hidden">
              <BrandMark className="h-20 w-20" />
              <span className="font-heading font-bold">Mayr-Technologies</span>
            </div>
            <button
              type="button"
              onClick={() => setLocale(locale === "de" ? "en" : "de")}
              data-testid="login-lang-toggle"
              className="ml-auto rounded-md px-2 py-1 text-xs font-medium uppercase text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              {locale === "de" ? "EN" : "DE"}
            </button>
          </div>

          <h1 className="font-heading text-2xl font-bold text-slate-900 dark:text-slate-50">{t("auth.login_title")}</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{t("auth.login_subtitle")}</p>

          {mfaChallenge ? (
          <form onSubmit={submitMfa} className="mt-8 space-y-4">
            <p className="text-sm text-slate-500">{t("auth.mfa_login_hint")}</p>
            <div className="space-y-1.5">
              <Label htmlFor="mfa-code">{t("auth.mfa_code")}</Label>
              <Input id="mfa-code" inputMode="numeric" autoComplete="one-time-code" required autoFocus
                value={mfaCode} onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                data-testid="login-mfa-code" />
            </div>
            {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950 dark:text-red-300" role="alert">{error}</p>}
            <Button type="submit" className="w-full" disabled={loading || mfaCode.length !== 6}>{t("auth.mfa_verify")}</Button>
            <button type="button" className="w-full text-sm text-slate-500 hover:underline"
              onClick={() => { setMfaChallenge(""); setMfaCode(""); setError(""); }}>{t("auth.back_to_login")}</button>
          </form>
          ) : (
          <form onSubmit={submit} className="mt-8 space-y-4" noValidate>
            <div className="space-y-1.5">
              <Label htmlFor="email">{t("auth.email")}</Label>
              <Input
                id="email" type="email" autoComplete="email" required value={email}
                onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">{t("auth.password")}</Label>
              <Input
                id="password" type="password" autoComplete="current-password" required value={password}
                onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input"
              />
            </div>
            {error && (
              <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950 dark:text-red-300" data-testid="login-error" role="alert">
                {error}
              </p>
            )}
            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
              {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t("auth.logging_in")}</> : t("auth.login_button")}
            </Button>
            <Link to="/forgot-password" className="block text-center text-sm text-primary hover:underline" data-testid="forgot-password-link">
              {t("auth.forgot_password")}
            </Link>
          </form>
          )}
        </div>
      </div>
    </div>
  );
}
